import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'react-feather';
import { toast } from 'react-toastify';
import {
  fetchTasksMe,
  RM_TASKS_ENABLED,
  tasksApi,
} from '../../../Tasks/tasksApi';
import {
  Button,
  Card,
  DescriptionList,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  Select,
  Table,
  TextInput,
  Toolbar,
} from '../../../ui';
import { Callout, IconButton, Segmented, StatTile, Swatch } from './ui-pending';
import type { StatTone } from './ui-pending';
import type { BadgeTone } from '../../../ui';
import './RenewalAnalyticsPage.scss';

/**
 * Consumer Xperience → Renewal Insights.
 *
 * Renewals are the business, so this screen is built around one honest
 * question: of the leads that arrived, how many did an RM reach, and how
 * many of those renewed?
 *
 * The thing that shapes the whole page: `plan_renewed` is stamped when the
 * payment lands, so a learner can renew with no RM contact at all. Those
 * renewals are reported *beside* the funnel rather than inside it — counting
 * them as agent conversions would credit people for payments they had no
 * hand in, which is the opposite of what a performance screen is for.
 */

interface FunnelStep {
  key: string;
  label: string;
  count: number;
  pctOfCreated: number;
  pctOfPrev: number;
}
interface Breakdown { key: string; label: string; count: number; pct: number }
interface AgentStats {
  externalId: string;
  name: string;
  created: number;
  worked: number;
  connected: number;
  converted: number;
  convertedAfterContact: number;
  convertedNoContact: number;
  declined: number;
  open: number;
  connectRate: number;
  conversionRate: number;
  closeRate: number;
  avgTouches: number;
  medianHoursToFirstTouch: number | null;
  slaMet: number;
  slaTotal: number;
}
interface Analytics {
  window: { from: string; to: string; tz: string; days: number };
  totals: {
    created: number; worked: number; connected: number; converted: number;
    convertedAfterContact: number; convertedNoContact: number;
    declined: number; open: number; closedOther: number;
  };
  rates: { connectRate: number; conversionRate: number; closeRate: number; declineRate: number };
  funnel: FunnelStep[];
  declineReasons: Breakdown[];
  actionTaken: Breakdown[];
  agents: AgentStats[];
  series: { bucket: string; created: number; converted: number; declined: number }[];
  effort: { activities: number; calls: number; talkMinutes: number; avgTouches: number };
  /**
   * What the renewals were worth, kept per currency.
   *
   * `amount` is the approximate rupee equivalent; `byCurrency` is what was
   * actually collected. Renewals are weighted to US/Canada and one Indian
   * renewal is worth roughly thirty dollar ones, so a single total tells
   * nobody what was sold where.
   */
  revenue?: {
    amount: number;
    currency: string | null;
    tickets: number;
    byCurrency: { currency: string; amount: number; tickets: number }[];
    ratesAsOf: string;
  };
  empty: boolean;
}

const num = (n: number): string => n.toLocaleString('en-IN');

/** Indian grouping for the rupee figure, plain grouping for the rest. */
const money = (n: number, currency: string): string =>
  n.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });

/** ₹28,87,043 reads better than 2887043 at a glance; keep the lakh grouping. */
const rupees = (n: number): string => `₹${n.toLocaleString('en-IN')}`;

/**
 * Swatch takes BadgeTone, which is narrower than the Meter palette — no
 * 'dark'. Seven is enough: eight currencies are possible but three or four
 * ever appear in one window.
 */
const CURRENCY_TONES: BadgeTone[] = ['success', 'info', 'primary', 'accent', 'warning', 'neutral', 'danger'];
const pctStr = (n: number): string => `${n}%`;

type MeterTone = 'primary' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'dark';

/** Decline reasons get a fixed tone sequence so a reason keeps its colour. */
const REASON_TONES: MeterTone[] = ['danger', 'warning', 'accent', 'primary', 'info', 'success', 'neutral', 'dark'];
/** Call outcomes, in the order the service returns them. */
const ACTION_TONES: MeterTone[] = ['success', 'info', 'warning', 'primary', 'danger'];

const dayLabel = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', timeZone: 'UTC',
  });
};

const isoDaysAgo = (n: number): string =>
  new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

const STAT_TONE: Record<'good' | 'bad' | 'warn' | 'plain', StatTone> = {
  good: 'success',
  bad: 'danger',
  warn: 'warning',
  plain: 'neutral',
};

/** One headline figure. */
const Stat: React.FC<{
  label: string; value: string; sub?: string; tone?: 'good' | 'bad' | 'warn' | 'plain'; title?: string;
}> = ({ label, value, sub, tone = 'plain', title }) => (
  <StatTile label={label} value={value} hint={sub} tone={STAT_TONE[tone]} title={title} />
);

/** Share-of-total bar with a label row above it. Width is data (allowed inline geometry). */
const Meter: React.FC<{ label: React.ReactNode; figure: React.ReactNode; pct: number; tone: MeterTone }> = ({
  label, figure, pct, tone,
}) => (
  <div>
    <div className="ra-meter__head">
      <span>{label}</span>
      <span className="ds-num">{figure}</span>
    </div>
    <div className="ra-meter__track">
      <div className={`ra-meter__fill ra-meter__fill--${tone}`} style={{ width: `${pct}%` }} />
    </div>
  </div>
);

const RenewalAnalyticsPage: React.FC = () => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [me, setMe] = useState<string>('');
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(isoDaysAgo(29));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [agent, setAgent] = useState('');
  const [pod, setPod] = useState('');
  const [country, setCountry] = useState('');
  const [pods, setPods] = useState<{ key: string; name: string }[]>([]);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);
  /**
   * The RM list, kept apart from the analytics response on purpose.
   *
   * It used to be read off `data.agents` — but that payload is already
   * scoped to whatever is selected, so choosing an RM collapsed the dropdown
   * to just that RM and you had to go back through "Whole team" to pick
   * anyone else. The roster has to come from somewhere the filter cannot
   * narrow. Each entry carries its desks so the list can follow the desk
   * filter without ever depending on the results.
   */
  const [roster, setRoster] = useState<{ externalId: string; name: string; pods: string[] }[]>([]);
  const [gran, setGran] = useState<'day' | 'week'>('day');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ from, to, granularity: gran });
      if (agent) p.set('agentExternalId', agent);
      // Both narrow the population every rate is computed from, not just the
      // list — so a conversion rate describes the slice that is on screen.
      if (pod) p.set('podKey', pod);
      if (country) p.set('country', country);
      setData(await tasksApi<Analytics>(`/v1/renewals/analytics?${p.toString()}`));
    } catch (err: any) {
      toast.error(err.message || 'Could not load renewal analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, agent, pod, country, gran]);

  useEffect(() => {
    fetchTasksMe().then((m) => {
      setAllowed(!!m);
      setMe(m?.externalId ?? '');
    });
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Desks and the countries that actually have learners, for the filters.
  useEffect(() => {
    tasksApi<{
      pods: {
        key: string;
        name: string;
        countries: { countryCode: string; name: string; learners: number }[];
        agents: { externalId: string; name: string; role: string; isActive: boolean }[];
      }[];
    }>('/v1/pods')
      .then((d) => {
        setPods((d.pods ?? []).map((x) => ({ key: x.key, name: x.name })));
        const byAgent = new Map<string, { externalId: string; name: string; pods: string[] }>();
        for (const pod of d.pods ?? []) {
          for (const a of pod.agents ?? []) {
            if (a.role !== 'AGENT' || !a.isActive) continue;
            const prev = byAgent.get(a.externalId);
            // An RM can sit on more than one desk; keep every desk they are on.
            if (prev) prev.pods.push(pod.key);
            else byAgent.set(a.externalId, { externalId: a.externalId, name: a.name, pods: [pod.key] });
          }
        }
        setRoster(Array.from(byAgent.values()).sort((x, y) => x.name.localeCompare(y.name)));
        const seen = new Map<string, { code: string; name: string; learners: number }>();
        for (const pod of d.pods ?? []) {
          for (const c of pod.countries ?? []) {
            const prev = seen.get(c.countryCode);
            // India sits in three desks on purpose — count each country once.
            if (!prev || c.learners > prev.learners) {
              seen.set(c.countryCode, { code: c.countryCode, name: c.name, learners: c.learners });
            }
          }
        }
        setCountries(
          Array.from(seen.values())
            .filter((c) => c.learners > 0)
            .sort((a, b) => b.learners - a.learners || a.name.localeCompare(b.name))
            .map(({ code, name }) => ({ code, name })),
        );
      })
      .catch(() => undefined);
  }, []);

  const maxSeries = useMemo(
    () => Math.max(1, ...(data?.series ?? []).map((s) => s.created)),
    [data],
  );

  const title = 'Renewal Insights';
  const subtitle = 'Of the leads that arrived, how many did we reach — and how many renewed.';

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        <PageHeader eyebrow="RM Tasks" title={title} />
        <EmptyState title="Not available yet" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }
  if (allowed === null) {
    return (
      <Page>
        <PageHeader eyebrow="RM Tasks" title={title} />
        <Loader />
      </Page>
    );
  }
  if (!allowed) {
    return (
      <Page>
        <PageHeader eyebrow="RM Tasks" title={title} />
        <EmptyState
          title="No RM record"
          description="Renewal insights are for the RM team — you don't have an RM record in this tool."
        />
      </Page>
    );
  }

  const t = data?.totals;
  const r = data?.rates;

  return (
    <Page>
      <PageHeader
        eyebrow="RM Tasks"
        title={title}
        subtitle={subtitle}
        actions={
          <IconButton onClick={() => void load()} title="Refresh" aria-label="Refresh">
            <RefreshCw size={16} aria-hidden />
          </IconButton>
        }
      />

      <Toolbar>
        <Field label="From">
          {(p) => (
            <TextInput {...p} type="date" size="sm" value={from}
              onChange={(e) => setFrom(e.target.value)} />
          )}
        </Field>
        <Field label="To">
          {(p) => (
            <TextInput {...p} type="date" size="sm" value={to}
              min={from} onChange={(e) => setTo(e.target.value)} />
          )}
        </Field>
        {/* Open to every RM, not just leaders. The By RM table below has
            always shown the whole team's numbers side by side — these
            only decide whose column you narrow to, so gating them hid
            the comparison rather than the data. Ops want the team
            comparing itself. */}
        <Field label="Desk">
          {(p) => (
            <Select {...p} size="sm" value={pod} onChange={(e) => setPod(e.target.value)}
              options={[{ value: '', label: 'All desks' }, ...pods.map((x) => ({ value: x.key, label: x.name }))]} />
          )}
        </Field>
        <Field label="Country">
          {(p) => (
            <Select {...p} size="sm" value={country} onChange={(e) => setCountry(e.target.value)}
              options={[{ value: '', label: 'All countries' }, ...countries.map((c) => ({ value: c.code, label: c.name }))]} />
          )}
        </Field>
        <Field label="RM">
          {(p) => (
            <Select {...p} size="sm" value={agent} onChange={(e) => setAgent(e.target.value)}
              options={[
                { value: '', label: 'Whole team' },
                ...roster
                  // Following the desk keeps the two filters honest together;
                  // the selected RM always stays listed so a pick never
                  // vanishes from the control that is showing it.
                  .filter((a) => !pod || a.pods.includes(pod) || a.externalId === agent)
                  .map((a) => ({ value: a.externalId, label: a.name })),
              ]} />
          )}
        </Field>
        <div className="ds-toolbar__end rm-row">
          {me && (
            <Button size="sm" variant={agent ? 'accent' : 'secondary'} aria-pressed={!!agent}
              onClick={() => setAgent(agent ? '' : me)}>
              {agent ? 'Showing just me' : 'Just me'}
            </Button>
          )}
          <Segmented<'day' | 'week'>
            ariaLabel="Granularity"
            size="sm"
            value={gran}
            onChange={setGran}
            options={[{ value: 'day', label: 'Daily' }, { value: 'week', label: 'Weekly' }]}
          />
        </div>
      </Toolbar>

      {loading ? (
        <Loader label="Loading renewal analytics…" />
      ) : !data ? (
        <EmptyState title="Nothing to show." description="The analytics could not be loaded. Try Refresh." />
      ) : data.empty ? (
        <Card>
          <EmptyState
            title="No renewal leads in this window"
            description="Renewal tickets started arriving on 5 October. Widen the dates, or come back once a few days of leads have built up."
          />
        </Card>
      ) : (
        <>
          {/* Headline numbers */}
          <div className="rm-stats">
            <Stat label="Leads" value={num(t!.created)} sub={`${data.window.days} days`} />
            {/* "Reached", not "Connected". The task list's Last-activity
                filter says Connected too and means the newest activity, so
                the same word in both places looked like a contradiction: a
                ticket connected on Monday with a follow-up logged on Tuesday
                is in this figure and not in that filter. */}
            <Stat label="Reached at least once" value={num(t!.connected)}
              sub={pctStr(r!.connectRate) + ' of leads'}
              tone={r!.connectRate < 50 ? 'warn' : 'plain'}
              title={
                'Tickets where an RM logged Connected at any point. Not the same as the task ' +
                "list's Last activity = Connected, which counts only tickets whose newest " +
                'activity is Connected — a ticket that connected and then got a follow-up is ' +
                'here but not there.'
              } />
            <Stat label="Renewed after contact" value={num(t!.convertedAfterContact)}
              sub={`${pctStr(r!.closeRate)} of connected`} tone="good"
              title="The RM got through and the learner then renewed — the part an RM can claim." />
            <Stat label="Renewed on their own" value={num(t!.convertedNoContact)}
              sub="no RM contact"
              title="Payment landed without the RM ever connecting. Not a win for the RM, not a failure either — shown separately so nobody is credited for it." />
            <Stat label="Declined" value={num(t!.declined)} sub={pctStr(r!.declineRate) + ' of leads'}
              tone={t!.declined > 0 ? 'bad' : 'plain'} />
            {/* Closed, but neither renewed nor declined — overwhelmingly
                "Will renew later", where the learner asked to be approached
                in a month or two. The service has always counted these in
                `closedOther`; nothing displayed it, so they were inside
                Leads and inside no other box, and the numbers did not add
                up. Hidden when there are none, so it stays quiet for a
                window where nobody parked anyone. */}
            {t!.closedOther > 0 && (
              <Stat label="Will renew later" value={num(t!.closedOther)}
                sub="parked for a date"
                tone="plain"
                title="Closed because the learner asked to be approached later, with the date recorded on the ticket. Neither won nor lost — they are not in the queue, and they have not said no." />
            )}
            <Stat label="Still open" value={num(t!.open)}
              sub={t!.open > 0 ? 'in the queue' : 'all resolved'}
              tone={t!.open > 0 ? 'warn' : 'good'} />
          </div>

          {/* What the renewals were worth.
              Deliberately its own band rather than another stat tile: it is
              the point of the funnel above, and the per-currency split has to
              sit beside the total or the total misleads. Renewals are
              weighted to US/Canada, and one Indian renewal is worth about
              thirty dollar ones — a single number hides which. */}
          {data.revenue && data.revenue.tickets > 0 && (
            <Card className="ra-revenue">
              <div className="ra-revenue__head">
                <div>
                  <div className="ra-revenue__label">Renewal value</div>
                  <div className="ra-revenue__total" title={`Converted at rates as of ${data.revenue.ratesAsOf}`}>
                    {rupees(data.revenue.amount)}
                    <span className="ra-revenue__approx">approx.</span>
                  </div>
                  <div className="ra-revenue__sub">
                    from {num(data.revenue.tickets)} of {num(t!.converted)} renewals
                    {data.revenue.tickets < t!.converted && (
                      <span
                        className="ra-revenue__gap"
                        title="The rest were closed without an amount being recorded, so this is a floor rather than the full figure."
                      >
                        {' '}· {num(t!.converted - data.revenue.tickets)} with no amount recorded
                      </span>
                    )}
                  </div>
                </div>
                <div className="ra-revenue__currencies">
                  {data.revenue.byCurrency.map((c, i) => (
                    <div key={c.currency} className="ra-cur">
                      <Swatch tone={CURRENCY_TONES[i % CURRENCY_TONES.length]} />
                      <div>
                        <div className="ra-cur__amount">
                          <span className="ra-cur__code">{c.currency}</span> {money(c.amount, c.currency)}
                        </div>
                        <div className="ra-cur__count">
                          {num(c.tickets)} renewal{c.tickets === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Said out loud rather than in a tooltip: the rupee figure is
                  a conversion, and anyone reading it as banked revenue should
                  know what it was converted at and when. */}
              <div className="ra-revenue__note">
                Collected amounts are shown in the currency they were paid in. The rupee total is
                approximate, converted at rates as of {data.revenue.ratesAsOf}.
              </div>
            </Card>
          )}

          <div className="row g-3">
            {/* Funnel */}
            <div className="col-12 col-xl-5">
              <Card className="ra-card-fill" title="The funnel"
                subtitle="Each bar is a share of the one above it, so the biggest drop is the thing to fix.">
                <div className="ra-meter-list">
                  {data.funnel.map((f, i) => (
                    <div key={f.key}>
                      <div className="ra-meter__head">
                        <span className="ds-strong">{f.label}</span>
                        <span className="ds-num">
                          {num(f.count)}
                          <span className="rm-caption"> · {pctStr(f.pctOfCreated)} of leads</span>
                        </span>
                      </div>
                      <div className="ra-meter__track ra-meter__track--lg">
                        <div
                          className={`ra-meter__fill ra-meter__fill--${i === data.funnel.length - 1 ? 'success' : 'primary'}`}
                          style={{ width: `${Math.max(f.pctOfCreated, f.count > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                      {i > 0 && (
                        <div className="rm-caption ra-meter__note">
                          {pctStr(f.pctOfPrev)} of {data.funnel[i - 1].label.toLowerCase()}
                          {f.pctOfPrev < 50 && <span className="ra-drop"> · biggest drop here</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {t!.convertedNoContact > 0 && (
                  <Callout tone="info" className="ra-callout">
                    <b>{num(t!.convertedNoContact)}</b> more renewed without any RM contact.
                    They are deliberately outside this funnel — the payment landed on its
                    own, so no RM is credited with it.
                  </Callout>
                )}
              </Card>
            </div>

            {/* Trend */}
            <div className="col-12 col-xl-7">
              <Card className="ra-card-fill" title="Leads and outcomes over time"
                subtitle="Bars are leads created; the green and red portions are the ones that have since renewed or declined.">
                <div className="ra-trend">
                  {data.series.map((s) => {
                    const h = (n: number) => `${(n / maxSeries) * 160}px`;
                    const other = s.created - s.converted - s.declined;
                    return (
                      <div key={s.bucket} className="ra-trend__col"
                        title={`${dayLabel(s.bucket)} — ${s.created} leads, ${s.converted} renewed, ${s.declined} declined`}>
                        <div className="ra-trend__stack">
                          <div className="ra-seg--renewed" style={{ height: h(s.converted) }} />
                          <div className="ra-seg--declined" style={{ height: h(s.declined) }} />
                          <div className="ra-seg--other" style={{ height: h(other) }} />
                        </div>
                        <div className="ra-trend__label">{dayLabel(s.bucket)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="ra-legend">
                  <span className="ra-legend__item"><Swatch tone="success" />Renewed</span>
                  <span className="ra-legend__item"><Swatch tone="danger" />Declined</span>
                  <span className="ra-legend__item">
                    <span className="rm-swatch rm-swatch--square ra-seg--other" aria-hidden />Open / other
                  </span>
                </div>
              </Card>
            </div>

            {/* Why they said no */}
            <div className="col-12 col-lg-6">
              <Card className="ra-card-fill" title="Why they declined"
                subtitle={
                  <>
                    The reason the RM recorded on tickets closed as Not interested.
                    {data.declineReasons.length === 0 && ' Nothing recorded in this window.'}
                  </>
                }>
                <div className="ra-meter-list">
                  {data.declineReasons.map((d, i) => (
                    <Meter key={d.key} label={d.label} pct={d.pct}
                      tone={REASON_TONES[i % REASON_TONES.length]}
                      figure={<>{num(d.count)} <span className="ds-text-muted">· {pctStr(d.pct)}</span></>} />
                  ))}
                </div>
              </Card>
            </div>

            {/* What RMs did */}
            <div className="col-12 col-lg-6">
              <Card className="ra-card-fill" title="What happened on the call"
                subtitle={
                  <>
                    The action the RM recorded after connecting.
                    {data.actionTaken.length === 0 && ' Nothing recorded in this window.'}
                  </>
                }>
                <div className="ra-meter-list">
                  {data.actionTaken.map((d, i) => (
                    <Meter key={d.key} label={d.label} pct={d.pct}
                      tone={ACTION_TONES[i % ACTION_TONES.length]}
                      figure={<>{num(d.count)} <span className="ds-text-muted">· {pctStr(d.pct)}</span></>} />
                  ))}
                </div>
                <div className="ra-effort">
                  <DescriptionList items={[
                    { label: 'Activities logged', value: num(data.effort.activities) },
                    { label: 'Calls', value: num(data.effort.calls) },
                    { label: 'Talk time', value: `${num(data.effort.talkMinutes)} min` },
                    { label: 'Touches / lead', value: data.effort.avgTouches },
                  ]} />
                </div>
              </Card>
            </div>

            {/* Agent table */}
            <div className="col-12">
              <Card title="By RM"
                subtitle={
                  <>
                    Two conversion columns on purpose. <b>Renewed</b> is of every lead they
                    held; <b>Close rate</b> is of the ones they actually reached. Someone who
                    connects with few learners but converts most of them looks weak on the
                    first and strong on the second — both are worth knowing.
                  </>
                }>
                <Table caption="Renewal performance by RM">
                  <thead>
                    <tr>
                      <th>RM</th>
                      <th className="ds-num">Leads</th>
                      <th className="ds-num" title="Tickets where they logged a Connected activity">Connected</th>
                      <th className="ds-num">Connect&nbsp;%</th>
                      <th className="ds-num" title="Renewed after the RM connected">Renewed</th>
                      <th className="ds-num" title="Renewed after contact ÷ connected">Close&nbsp;%</th>
                      <th className="ds-num" title="Renewed with no RM contact — not credited to them">Self-serve</th>
                      <th className="ds-num">Declined</th>
                      <th className="ds-num">Open</th>
                      <th className="ds-num" title="Activities logged per lead — effort, not outcome">Touches</th>
                      <th className="ds-num" title="Median hours from the lead arriving to their first action">1st touch</th>
                      <th className="ds-num" title="Actioned within the SLA deadline">SLA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.agents.map((a) => {
                      const sla = a.slaTotal ? Math.round((a.slaMet / a.slaTotal) * 100) : null;
                      return (
                        <tr key={a.externalId || a.name}>
                          <td className="ds-strong">{a.name || 'Unassigned'}</td>
                          <td className="ds-num">{num(a.created)}</td>
                          <td className="ds-num">{num(a.connected)}</td>
                          <td className={`ds-num${a.connectRate < 50 ? ' ra-cell--warning' : ''}`}>
                            {pctStr(a.connectRate)}
                          </td>
                          <td className={`ds-num ds-strong${a.convertedAfterContact ? ' ra-cell--success' : ''}`}>
                            {num(a.convertedAfterContact)}
                          </td>
                          <td className="ds-num">
                            {a.connected ? pctStr(a.closeRate) : <span className="ds-text-muted">—</span>}
                          </td>
                          <td className="ds-num ds-text-muted">{num(a.convertedNoContact)}</td>
                          <td className={`ds-num${a.declined ? ' ra-cell--danger' : ''}`}>{num(a.declined)}</td>
                          <td className="ds-num">{num(a.open)}</td>
                          <td className="ds-num ds-text-muted">{a.avgTouches}</td>
                          <td className="ds-num ds-text-muted">
                            {a.medianHoursToFirstTouch === null ? '—' : `${a.medianHoursToFirstTouch}h`}
                          </td>
                          <td className={`ds-num${sla !== null && sla < 80 ? ' ra-cell--danger' : ''}`}>
                            {sla === null ? '—' : `${sla}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card>
            </div>
          </div>

          <p className="rm-caption">
            {data.window.from} to {data.window.to} ({data.window.tz}). Every figure is
            derived from what RMs logged on the tickets — &ldquo;connected&rdquo; means a
            Connected activity exists, &ldquo;renewed&rdquo; means the ticket closed as
            Plan renewed. Nothing here is a separate counter that could drift.
          </p>
        </>
      )}
    </Page>
  );
};

export default RenewalAnalyticsPage;
